import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import {
  Users,
  UserPlus,
  Shield,
  Trash2,
  Crown,
  UserCog,
  Truck,
  AlertCircle,
  X,
  RefreshCw,
  Mail,
  Clock
} from 'lucide-react';

const ROLE_CONFIG = {
  super_admin: {
    label: 'Super Admin',
    description: 'Accès complet + gestion des admins',
    icon: Crown,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100'
  },
  admin: {
    label: 'Admin',
    description: 'Accès complet au backoffice',
    icon: Shield,
    color: 'text-red-600',
    bgColor: 'bg-red-100'
  },
  manager: {
    label: 'Manager',
    description: 'Gestion des commandes et clients',
    icon: UserCog,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100'
  },
  delivery: {
    label: 'Livreur',
    description: 'Accès mode livraison uniquement',
    icon: Truck,
    color: 'text-green-600',
    bgColor: 'bg-green-100'
  }
};

export const AdminManagement = () => {
  const { isSuperAdmin, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', role: 'admin' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      loadAdmins();
    } else {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const listAdmins = httpsCallable(functions, 'listAdmins');
      const result = await listAdmins();
      setAdmins(result.data?.admins || []);
    } catch (error) {
      console.error('Error loading admins:', error);
      setError('Erreur lors du chargement des administrateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.email) {
      setError('Email requis');
      return;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdmin.email)) {
      setError('Format d\'email invalide');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const setAdminClaim = httpsCallable(functions, 'setAdminClaim');
      await setAdminClaim({
        targetEmail: newAdmin.email,
        role: newAdmin.role
      });

      setSuccess(`${newAdmin.email} a été ajouté comme ${ROLE_CONFIG[newAdmin.role].label}`);
      setShowAddModal(false);
      setNewAdmin({ email: '', role: 'admin' });
      loadAdmins();

      // Clear success message after 3s
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error adding admin:', error);
      if (error.message?.includes('not found')) {
        setError('Utilisateur non trouvé. Il doit d\'abord créer un compte Firebase.');
      } else {
        setError(error.message || 'Erreur lors de l\'ajout');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAdmin = async (admin) => {
    if (admin.uid === user?.uid) {
      setError('Vous ne pouvez pas révoquer vos propres droits');
      return;
    }

    if (admin.role === 'super_admin') {
      setError('Impossible de révoquer un Super Admin');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir retirer les droits admin de ${admin.email} ?`)) {
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const revokeAdminClaim = httpsCallable(functions, 'revokeAdminClaim');
      await revokeAdminClaim({ targetUid: admin.uid });
      setSuccess(`Droits révoqués pour ${admin.email}`);
      loadAdmins();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error revoking admin:', error);
      setError(error.message || 'Erreur lors de la révocation');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Non super_admin
  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center max-w-md mx-auto">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Accès restreint</h2>
          <p className="text-gray-500">
            Seuls les Super Admins peuvent gérer les administrateurs.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-red-600" />
            Gestion des Admins
          </h1>
          <p className="text-gray-500 mt-1">Gérez les accès au backoffice</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={loadAdmins}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Ajouter un admin
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <Shield className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Liste des admins */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full" />
        </div>
      ) : admins.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Aucun administrateur trouvé</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {admins.map((admin) => {
            const roleConfig = ROLE_CONFIG[admin.role] || ROLE_CONFIG.admin;
            const RoleIcon = roleConfig.icon;
            const isCurrentUser = admin.uid === user?.uid;

            return (
              <Card key={admin.uid} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${roleConfig.bgColor}`}>
                      <RoleIcon className={`w-6 h-6 ${roleConfig.color}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">{admin.email}</p>
                        {isCurrentUser && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                            Vous
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-sm font-medium ${roleConfig.color}`}>
                          {roleConfig.label}
                        </span>
                        {admin.updatedAt && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(admin.updatedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {admin.role !== 'super_admin' && !isCurrentUser && (
                      <Button
                        variant="ghost"
                        onClick={() => handleRevokeAdmin(admin)}
                        disabled={actionLoading}
                        className="text-red-500 hover:bg-red-50 p-2"
                        title="Révoquer les droits"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Légende des rôles */}
      <Card className="mt-8 p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Légende des rôles</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(ROLE_CONFIG).map(([key, config]) => {
            const RoleIcon = config.icon;
            return (
              <div key={key} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.bgColor}`}>
                  <RoleIcon className={`w-4 h-4 ${config.color}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{config.label}</p>
                  <p className="text-xs text-gray-500">{config.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Modal Ajouter Admin */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <UserPlus className="w-6 h-6 text-red-600" />
                Ajouter un administrateur
              </h2>
              <button
                onClick={() => { setShowAddModal(false); setError(''); }}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              L'utilisateur doit avoir un compte Firebase existant.
            </p>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 flex items-center gap-2 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de l'utilisateur
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    placeholder="utilisateur@lefamilys.fr"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle
                </label>
                <select
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none"
                >
                  <option value="admin">Admin - Accès complet</option>
                  <option value="manager">Manager - Commandes & clients</option>
                  <option value="delivery">Livreur - Mode livraison</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => { setShowAddModal(false); setError(''); }}
                className="flex-1"
                disabled={actionLoading}
              >
                Annuler
              </Button>
              <Button
                onClick={handleAddAdmin}
                className="flex-1"
                disabled={actionLoading || !newAdmin.email}
              >
                {actionLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Ajout...
                  </span>
                ) : (
                  'Ajouter'
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
